import { Component, OnInit, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';
import {
  FiltroDefinidoSalvo,
  FiltrosDefinidosStorageService,
} from 'src/app/service/filtros-definidos-storage.service';
import {
  avaliarCondicoesFiltroDefinido,
  extrairPartesValorCelula,
  FiltroDefinidoCondicao,
  obterLabelOperador,
  operadorExigeValor,
} from 'src/app/utils/filtro-definido-condicoes.util';

interface CondicaoAplicadaLinha {
  conector: string | null;
  descricao: string;
}

interface ColunaCondicaoResumo {
  id: string;
  label: string;
  abaTitulo: string;
}

interface Cliente {
  sobrenome: string;
  nome: string;
  cpf: string;
  contato: string;
  tarefa: string;
  email: string;
  pastaApelido: string;
  pastaLink: string;
  dataCadastro: string;
  pastaFisica: boolean;
  servicos: string[];
  perfil?: string;
  /** Registro da API (após converterParaLowercase) para `cadastro-cliente` em modo edição */
  dadosParaEdicao: Record<string, unknown>;
}

interface ColunaTabelaGestao {
  id: string;
  key: string;
  label: string;
  cssClass: string;
  ordenavel?: boolean;
  tipo?: 'editar' | 'pasta' | 'texto' | 'data' | 'sigla';
}

@Component({
  selector: 'app-home-gestao',
  templateUrl: './home-gestao.component.html',
  styleUrls: ['./home-gestao.component.scss']
})
export class HomeGestaoComponent implements OnInit {

  filtros = {
    nome: '',
    perfil: ''
  };

  /** Cache estável para o template (evita loop de change detection no *ngFor). */
  colunasTabelaCache: ColunaTabelaGestao[] = [];
  tabelaComFiltroDinamico = false;

  clientes: Cliente[] = [];
  clientesFiltrados: Cliente[] = [];
  /** Linhas exibidas na página atual (slice de `clientesFiltrados`). */
  clientesPaginados: Cliente[] = [];
  termoPesquisa = '';
  private pesquisaTabelaTimeout: ReturnType<typeof setTimeout> | null = null;

  paginaAtual = 1;
  itensPorPagina = 10;
  totalItens = 0;
  totalPaginas = 0;

  totalClientes = 0;
  clientesComPasta = 0;
  totalTarefas = 0;

  /** Presets salvos na API (`FILTRO_DEFINIDO_EMPRESA`). */
  filtrosSalvos: FiltroDefinidoSalvo[] = [];

  modalSelecionarFiltroRef: NgbModalRef | null = null;
  /** Espelho do `filtros.perfil` enquanto o modal está aberto (confirmação). */
  perfilSelecaoModal = '';
  termoBuscaModalGestao = '';
  arrastandoTabela = false;
  private scrollInicioX = 0;
  private scrollEsquerdaInicial = 0;
  readonly colunasPadrao: ColunaTabelaGestao[] = [
    { id: 'editar', key: 'editar', label: 'Editar', cssClass: 'col-editar', tipo: 'editar' },
    { id: 'sobrenome', key: 'sobrenome', label: 'Sobrenome', cssClass: 'col-sobrenome', ordenavel: true, tipo: 'texto' },
    { id: 'nome', key: 'nome', label: 'Nome', cssClass: 'col-nome', ordenavel: true, tipo: 'texto' },
    { id: 'cpf', key: 'cpf', label: 'CPF', cssClass: 'col-cpf', ordenavel: true, tipo: 'texto' },
    { id: 'contato', key: 'contato', label: 'Contato', cssClass: 'col-contato', ordenavel: true, tipo: 'texto' },
    { id: 'servicos', key: 'servicos', label: 'Serviços do cliente', cssClass: 'col-servicos', tipo: 'texto' },
    { id: 'pasta', key: 'pasta', label: 'Pasta', cssClass: 'col-pasta', tipo: 'pasta' },
    { id: 'email', key: 'email', label: 'Email', cssClass: 'col-email', ordenavel: true, tipo: 'texto' },
  ];

  constructor(
    private ajaxService: AjaxService,
    private utilService: UtilService,
    private router: Router,
    private filtrosDefinidosStorage: FiltrosDefinidosStorageService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.colunasTabelaCache = [...this.colunasPadrao];
    this.recarregarFiltrosSalvos();
    this.carregarClientes();
  }

  /** Atualiza a lista de filtros criados na tela «Filtros definidos». */
  recarregarFiltrosSalvos(): void {
    this.filtrosDefinidosStorage.listar().subscribe({
      next: (lista) => {
        this.filtrosSalvos = [...lista].sort((a, b) => (a.criadoEm < b.criadoEm ? 1 : -1));
        const idSel = (this.filtros.perfil || '').trim();
        if (idSel && !this.filtrosSalvos.some((f) => f.id === idSel)) {
          this.filtros.perfil = '';
        }
        this.atualizarCacheColunasTabela();
      },
      error: () => {
        this.filtrosSalvos = [];
      },
    });
  }

  get textoResumoFiltroDefinido(): string {
    const id = (this.filtros.perfil || '').trim();
    if (!id) {
      return 'Selecionar filtro definido…';
    }
    const f = this.filtrosSalvos.find((x) => x.id === id);
    return f?.titulo || 'Filtro selecionado';
  }

  get filtrosSalvosFiltradosModalGestao(): FiltroDefinidoSalvo[] {
    const t = this.termoBuscaModalGestao.trim().toLowerCase();
    if (!t) {
      return this.filtrosSalvos;
    }
    return this.filtrosSalvos.filter((f) => (f.titulo || '').toLowerCase().includes(t));
  }

  abrirModalSelecionarFiltro(content: TemplateRef<unknown>): void {
    this.perfilSelecaoModal = (this.filtros.perfil || '').trim();
    this.termoBuscaModalGestao = '';
    this.modalSelecionarFiltroRef = this.modalService.open(content, {
      size: 'lg',
      centered: true,
    });
    this.modalSelecionarFiltroRef.result.then(
      () => undefined,
      () => undefined
    ).then(() => {
      this.modalSelecionarFiltroRef = null;
    });
  }

  fecharModalSelecionarFiltro(): void {
    this.modalSelecionarFiltroRef?.dismiss();
  }

  selecionarFiltroNoModal(f: FiltroDefinidoSalvo): void {
    this.perfilSelecaoModal = String(f.id ?? '');
  }

  isFiltroSelecionadoNoModal(f: FiltroDefinidoSalvo): boolean {
    return this.perfilSelecaoModal === String(f.id ?? '');
  }

  obterQtdCondicoesFiltro(f: FiltroDefinidoSalvo): number {
    const fr = f?.filtroRegistros;
    if (!fr || fr.mostrarTodos) {
      return 0;
    }
    return (fr.condicoes || []).filter((c) => c.abaId && c.key && c.operador).length;
  }

  textoQtdCondicoesFiltro(f: FiltroDefinidoSalvo): string | null {
    const qtd = this.obterQtdCondicoesFiltro(f);
    if (qtd <= 0) {
      return null;
    }
    return qtd === 1 ? '1 condição' : `${qtd} condições`;
  }

  confirmarFiltroModal(): void {
    this.filtros.perfil = (this.perfilSelecaoModal || '').trim();
    this.atualizarCacheColunasTabela();
    this.modalSelecionarFiltroRef?.close('ok');
    setTimeout(() => this.carregarClientes(), 0);
  }

  limparFiltroDefinidoModal(): void {
    this.perfilSelecaoModal = '';
    this.filtros.perfil = '';
    this.atualizarCacheColunasTabela();
    this.modalSelecionarFiltroRef?.close('clear');
    setTimeout(() => this.carregarClientes(), 0);
  }

  /** Retorna o preset selecionado (colunas da tabela e condições de registros). */
  get filtroDefinidoAtivo(): FiltroDefinidoSalvo | null {
    const id = (this.filtros.perfil || '').trim();
    if (!id) {
      return null;
    }
    return this.filtrosSalvos.find((f) => f.id === id) ?? null;
  }

  get filtroRegistrosMostraTodos(): boolean {
    const fr = this.filtroDefinidoAtivo?.filtroRegistros;
    return !fr || fr.mostrarTodos !== false;
  }

  /** Colunas distintas referenciadas nas condições ativas (ex.: Sigla, Data). */
  get colunasUsadasNasCondicoes(): ColunaCondicaoResumo[] {
    const filtro = this.filtroDefinidoAtivo;
    if (!filtro || this.filtroRegistrosMostraTodos) {
      return [];
    }
    const condicoes = (filtro.filtroRegistros?.condicoes || []).filter(
      (c) => c.abaId && c.key && c.operador
    );
    const vistos = new Set<string>();
    const resultado: ColunaCondicaoResumo[] = [];
    for (const condicao of condicoes) {
      const id = `${condicao.abaId}:${condicao.key}`;
      if (vistos.has(id)) {
        continue;
      }
      vistos.add(id);
      const item = filtro.itens?.find(
        (i) => i.abaId === condicao.abaId && i.key === condicao.key
      );
      resultado.push({
        id,
        label: item?.label || condicao.key,
        abaTitulo: item?.abaTitulo || condicao.abaId,
      });
    }
    return resultado;
  }

  trackByColunaCondicaoResumo(_index: number, coluna: ColunaCondicaoResumo): string {
    return coluna.id;
  }

  get linhasCondicoesAplicadas(): CondicaoAplicadaLinha[] {
    const filtro = this.filtroDefinidoAtivo;
    const condicoes = filtro?.filtroRegistros?.condicoes || [];
    return condicoes
      .filter((c) => c.abaId && c.key && c.operador)
      .map((condicao, index) => ({
        conector:
          index === 0 ? null : condicao.conector === 'ou' ? 'Ou' : 'E',
        descricao: this.formatarDescricaoCondicao(condicao, filtro!),
      }));
  }

  trackByCondicaoAplicada(index: number, linha: CondicaoAplicadaLinha): string {
    return `${index}-${linha.descricao}`;
  }

  /** Lista todos os clientes ativos da empresa (PACIENTE_CLINICA + usuário IS_ATIVO). */
  carregarClientes(): void {
    this.utilService.showLoading();
    const filtroAtivo = this.filtroDefinidoAtivo;
    let url = `${API}empresa/Paciente/ConsultarClienteUsuario?ativo=true`;
    if (filtroAtivo) {
      const keys = filtroAtivo.itens.map((i) => i.key).filter((x) => !!x).join(',');
      url += `&idFiltroDefinido=${encodeURIComponent(filtroAtivo.id)}`;
      if (keys) {
        url += `&colunas=${encodeURIComponent(keys)}`;
      }
    }

    this.ajaxService.get(url).subscribe({
      next: (response: any) => {
        this.utilService.closeLoading();
        const lista = this.extrairListaDaResposta(response);
        this.clientes = lista
          .filter((x) => x && typeof x === 'object')
          .map((x) => this.mapearPacienteParaCliente(this.utilService.converterParaLowercase(x)));
        this.atualizarCacheColunasTabela();
        this.pesquisarTabela();
        this.calcularEstatisticas();
      },
      error: () => {
        this.utilService.closeLoading();
        this.utilService.toasterShowError('Erro', 'Não foi possível carregar os clientes.');
        this.clientes = [];
        this.clientesFiltrados = [];
        this.aplicarPaginacao();
        this.calcularEstatisticas();
      }
    });
  }

  private extrairListaDaResposta(response: any): any[] {
    let atual: any = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    if (Array.isArray(atual)) return atual;
    return [];
  }

  private mapearPacienteParaCliente(c: Record<string, unknown>): Cliente {
    const sobrenome = String(c['sobrenome'] ?? '').trim();
    const nome = String(c['nome'] ?? '').trim() || '-';
    const email = String(c['email'] ?? '');
    const cpf = String(c['cpf'] ?? '-');
    const contato = String(c['telefone_celular'] ?? c['telefonecelular'] ?? c['telefone_adicional'] ?? c['telefoneadicional'] ?? '-');
    const dataCadastroRaw = c['data_cadastro'] ?? c['datacadastro'] ?? c['dataCadastro'] ?? c['data'] ?? '';
    const pastaFisica = Boolean(c['pasta_fisica'] ?? c['pastafisica']);
    const pastaLink = String(c['link_pasta'] ?? c['linkpasta'] ?? c['link_pasta_virtual'] ?? '').trim();
    const pastaApelido = String(c['apelido_pasta_virtual'] ?? c['apelidopastavirtual'] ?? '').trim();
    return {
      sobrenome: sobrenome || '-',
      nome,
      cpf: cpf || '-',
      contato: contato || '-',
      tarefa: '-',
      email,
      pastaApelido: pastaApelido || (pastaLink ? 'Abrir pasta' : '-'),
      pastaLink,
      dataCadastro: this.formatarDataCadastro(dataCadastroRaw),
      pastaFisica,
      servicos: this.extrairServicosHabilitados(c),
      perfil: 'cliente',
      dadosParaEdicao: { ...c }
    };
  }

  /** Abas marcadas em «Habilitar Abas» (PACIENTE_CLINICA.HABILITAR_*). */
  private extrairServicosHabilitados(c: Record<string, unknown>): string[] {
    const pares: [string, string][] = [
      ['habilitar_naturalizacao', 'Naturalização'],
      ['habilitar_cidadania', 'Cidadania'],
      ['habilitar_aire', 'AIRE'],
      ['habilitar_fast_it', 'Fast-It'],
      ['habilitar_traducao', 'Tradução'],
      ['habilitar_busca_documentos', 'Busca de Documentos'],
    ];
    const servicos: string[] = [];
    for (const [chave, label] of pares) {
      const v =
        c[chave] ??
        c[chave.replace(/_/g, '')] ??
        c[chave.toUpperCase()];
      if (this.utilService.parseBoolean(v)) {
        servicos.push(label);
      }
    }
    return servicos;
  }

  private formatarDataCadastro(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    if (!texto) return '-';
    const data = new Date(texto);
    if (Number.isNaN(data.getTime())) return texto;
    return data.toLocaleDateString('pt-BR');
  }

  aplicarPaginacao(): void {
    const ipp = this.itensPorPaginaEfetivo();
    this.totalItens = this.clientesFiltrados.length;
    this.totalPaginas = Math.ceil(this.totalItens / ipp);

    if (this.paginaAtual > this.totalPaginas && this.totalPaginas > 0) {
      this.paginaAtual = this.totalPaginas;
    }
    if (this.paginaAtual < 1) {
      this.paginaAtual = 1;
    }

    const inicio = (this.paginaAtual - 1) * ipp;
    this.clientesPaginados = this.clientesFiltrados.slice(inicio, inicio + ipp);
  }

  /** Usado no template (texto "Mostrando X a Y"). */
  itensPorPaginaEfetivo(): number {
    const n = Number(this.itensPorPagina);
    return n > 0 ? n : 10;
  }

  getFimItensExibidos(): number {
    return Math.min(this.paginaAtual * this.itensPorPaginaEfetivo(), this.totalItens);
  }

  getNumerosPaginas(): number[] {
    const numeros: number[] = [];
    const inicio = Math.max(1, this.paginaAtual - 2);
    const fim = Math.min(this.totalPaginas, this.paginaAtual + 2);
    for (let i = inicio; i <= fim; i++) {
      numeros.push(i);
    }
    return numeros;
  }

  irParaPagina(pagina: number): void {
    if (pagina >= 1 && pagina <= this.totalPaginas) {
      this.paginaAtual = pagina;
      this.aplicarPaginacao();
    }
  }

  paginaAnterior(): void {
    if (this.paginaAtual > 1) {
      this.paginaAtual--;
      this.aplicarPaginacao();
    }
  }

  proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) {
      this.paginaAtual++;
      this.aplicarPaginacao();
    }
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.aplicarPaginacao();
  }

  pesquisarClientes(): void {
    this.paginaAtual = 1;
    this.clientesFiltrados = this.filtrarClientesComCriterios();
    this.aplicarPaginacao();
  }

  atualizarFiltros(): void {
    this.recarregarFiltrosSalvos();
    this.carregarClientes();
  }

  irParaFiltrosDefinidos(): void {
    this.router.navigate(['/filtros-definidos']);
  }

  get temFiltroAtivo(): boolean {
    return !!(this.filtros.perfil || '').trim() || !!(this.termoPesquisa || '').trim();
  }

  limparFiltros(): void {
    this.filtros = {
      nome: '',
      perfil: ''
    };
    this.termoPesquisa = '';
    this.paginaAtual = 1;
    this.atualizarCacheColunasTabela();
    this.carregarClientes();
  }

  onTermoPesquisaChange(): void {
    if (this.pesquisaTabelaTimeout != null) {
      clearTimeout(this.pesquisaTabelaTimeout);
    }
    this.pesquisaTabelaTimeout = setTimeout(() => {
      this.pesquisaTabelaTimeout = null;
      this.pesquisarTabela();
    }, 200);
  }

  pesquisarTabela(): void {
    this.paginaAtual = 1;
    const termo = (this.termoPesquisa || '').trim();
    const base = this.filtrarClientesComCriterios();
    if (!termo) {
      this.clientesFiltrados = base;
      this.aplicarPaginacao();
      return;
    }
    const t = termo.toLowerCase();
    this.clientesFiltrados = base.filter((cliente) =>
      this.colunasTabelaCache.some((coluna) => {
        if (coluna.tipo === 'editar') {
          return false;
        }
        const valor = this.getValorColunaTabela(cliente, coluna.key);
        return valor.toLowerCase().includes(t);
      }),
    );
    this.aplicarPaginacao();
  }

  trackByColunaId(_index: number, coluna: ColunaTabelaGestao): string {
    return coluna.id;
  }

  trackByItemLista(_index: number, item: string): string {
    return item;
  }

  /** Valores múltiplos (inclusive serviços) são exibidos em linhas separadas. */
  colunaExibeValoresEmLista(coluna: ColunaTabelaGestao): boolean {
    return coluna.tipo === 'data' || coluna.tipo === 'sigla' || coluna.key === 'servicos';
  }

  /** Retorna itens para empilhar na célula; vazio se valor único ou coluna não aplicável. */
  getItensValorColunaLista(cliente: Cliente, coluna: ColunaTabelaGestao): string[] {
    if (!this.colunaExibeValoresEmLista(coluna)) {
      return [];
    }
    const valor = this.getValorColunaTabela(cliente, coluna.key);
    if (!valor || valor === '-') {
      return [];
    }
    // Sigla e Data vêm pareadas por posição do backend (mesma ordem, "-" quando sem data):
    // não reordenar nem descartar itens, senão as linhas da célula deixam de corresponder.
    const partes = coluna.tipo === 'data' || coluna.tipo === 'sigla'
      ? valor.split(',').map((p) => p.trim()).filter((p) => p.length > 0)
      : extrairPartesValorCelula(valor);
    if (partes.length <= 1) {
      return [];
    }
    return partes;
  }

  private filtrarClientesComCriterios(): Cliente[] {
    return this.clientes.filter((cliente) => {
      const nomeFiltro = (this.filtros.nome || '').trim().toLowerCase();
      if (nomeFiltro) {
        const textoCliente = [cliente.sobrenome, cliente.nome]
          .filter((v) => v && v !== '-')
          .join(' ')
          .toLowerCase();
        if (!textoCliente.includes(nomeFiltro)) {
          return false;
        }
      }

      const filtroPreset = this.filtroDefinidoAtivo;
      const filtroRegistros = filtroPreset?.filtroRegistros;
      if (filtroRegistros && !filtroRegistros.mostrarTodos) {
        const atende = avaliarCondicoesFiltroDefinido(
          filtroRegistros.condicoes,
          (_abaId, key) => this.getValorColunaTabela(cliente, key)
        );
        if (!atende) {
          return false;
        }
      }

      return true;
    });
  }

  /** Abre o cadastro do cliente em modal (mantém filtro da listagem). */
  modalEdicaoAberto = false;
  clienteEdicaoId = 0;
  clienteEdicaoDados: Record<string, unknown> | null = null;

  /** Abre o cadastro do cliente em edição (mesmo fluxo de consultar-cliente). */
  editarCliente(cliente: Cliente): void {
    const dados = cliente.dadosParaEdicao;
    if (!dados || Object.keys(dados).length === 0) {
      this.utilService.toasterShowError('Atenção', 'Dados do cliente indisponíveis para edição.');
      return;
    }
    const idRaw =
      dados['id_paciente_clinica'] ?? dados['idpacienteclinica'] ?? dados['id'] ?? 0;
    const idPaciente = Number(idRaw) || 0;
    this.clienteEdicaoId = idPaciente;
    this.clienteEdicaoDados = { ...dados };
    this.modalEdicaoAberto = true;
  }

  fecharModalEdicao(): void {
    this.modalEdicaoAberto = false;
    this.clienteEdicaoDados = null;
    this.clienteEdicaoId = 0;
    // Recarrega a lista mantendo filtro definido / busca.
    this.carregarClientes();
  }

  ordenarTabela(campo: string): void {
    console.log('Ordenando por:', campo);
  }

  /** Recalcula colunas da tabela (chamar ao mudar o preset). */
  private atualizarCacheColunasTabela(): void {
    const filtro = this.filtroDefinidoAtivo;
    if (!filtro || !Array.isArray(filtro.itens) || filtro.itens.length === 0) {
      this.tabelaComFiltroDinamico = false;
      this.colunasTabelaCache = [...this.colunasPadrao];
      return;
    }

    const dinamicas: ColunaTabelaGestao[] = [];
    const vistos = new Set<string>();
    dinamicas.push(this.colunasPadrao[0]);

    for (const item of filtro.itens) {
      const key = String(item?.key || '').trim();
      if (!key) {
        continue;
      }
      const id = `${item.abaId || ''}:${key}`;
      if (vistos.has(id)) {
        continue;
      }
      vistos.add(id);
      dinamicas.push({
        id,
        key,
        label: String(item?.label || key),
        cssClass: this.getCssColunaDinamica(key),
        ordenavel: ['sobrenome', 'nome', 'cpf', 'contato', 'email'].includes(key),
        tipo: key === 'link_pasta' || key === 'apelido_pasta_virtual'
          ? 'pasta'
          : this.normalizarChave(key) === 'sigla'
            ? 'sigla'
          : this.isCampoData(key)
            ? 'data'
            : 'texto',
      });
    }

    const colunas = dinamicas.length > 1 ? dinamicas : [...this.colunasPadrao];
    this.tabelaComFiltroDinamico = dinamicas.length > 1;
    this.colunasTabelaCache = colunas;
  }

  private formatarDescricaoCondicao(
    condicao: FiltroDefinidoCondicao,
    filtro: FiltroDefinidoSalvo
  ): string {
    const item = filtro.itens?.find(
      (i) => i.abaId === condicao.abaId && i.key === condicao.key
    );
    const coluna = item?.label || condicao.key;
    const operador = obterLabelOperador(condicao.operador);
    if (!operadorExigeValor(condicao.operador)) {
      return `${coluna} ${operador}`;
    }
    const valor = (condicao.valor || '').trim();
    return `${coluna} ${operador} «${valor}»`;
  }

  getValorColunaTabela(cliente: Cliente, key: string): string {
    let valor: string;
    switch (key) {
      case 'sobrenome':
        valor = cliente.sobrenome || '-';
        break;
      case 'nome':
        valor = cliente.nome || '-';
        break;
      case 'cpf':
        valor = cliente.cpf || '-';
        break;
      case 'contato':
      case 'telefone_celular':
      case 'telefone_adicional':
        valor = cliente.contato || '-';
        break;
      case 'email':
        valor = cliente.email || '-';
        break;
      case 'pasta_fisica':
        valor = cliente.pastaFisica ? 'Sim' : 'Não';
        break;
      case 'apelido_pasta_virtual':
        valor = cliente.pastaApelido || '-';
        break;
      case 'link_pasta':
        valor = cliente.pastaLink || '-';
        break;
      case 'carteira':
      case 'servicos':
        valor = this.formatarServicos(cliente.servicos || []);
        break;
      case 'data_cadastro':
      case 'datacadastro':
        valor = cliente.dataCadastro || '-';
        break;
      default:
        valor = this.getValorRawPorChave(cliente.dadosParaEdicao, key);
        break;
    }
    return this.formatarValorTabelaPorChave(key, valor);
  }

  private getCssColunaDinamica(key: string): string {
    const k = this.normalizarChave(key);
    if (k.includes('cpf')) return 'col-cpf';
    if (k.includes('email')) return 'col-email';
    if (k.includes('telefone') || k.includes('contato')) return 'col-contato';
    if (k.includes('sobrenome')) return 'col-sobrenome';
    if (k.includes('nome')) return 'col-nome';
    if (k.includes('servico')) return 'col-servicos';
    if (k.includes('pasta')) return 'col-pasta';
    return 'col-dinamica';
  }

  private getValorRawPorChave(raw: Record<string, unknown>, key: string): string {
    if (!raw || typeof raw !== 'object') {
      return '-';
    }
    const alvo = this.normalizarChave(key);
    const entrada = Object.entries(raw).find(([k]) => this.normalizarChave(k) === alvo);
    if (!entrada) {
      return '-';
    }
    const valor = entrada[1];
    if (valor == null) {
      return '-';
    }
    if (Array.isArray(valor)) {
      return valor.map((v) => String(v)).join(', ');
    }
    if (typeof valor === 'boolean') {
      return valor ? 'Sim' : 'Não';
    }
    return String(valor).trim() || '-';
  }

  private formatarValorTabelaPorChave(key: string, valor: string): string {
    const texto = String(valor ?? '').trim();
    if (!texto || texto === '-') {
      return '-';
    }
    if (!this.isCampoData(key)) {
      return texto;
    }
    return this.formatarDataPadraoBr(texto);
  }

  private isCampoData(key: string): boolean {
    const k = this.normalizarChave(key);
    const camposData = [
      'data',
      'prazo',
      'invio',
      'accettazione',
      'consegna',
      'notifica',
      'giuramento',
      'remessa',
      'ajuizamento',
      'audiencia',
      'sentenca',
      'richiesto',
      'ritirato',
      'servicopago',
      'inscricao',
      'demanda',
      'limite',
      'cadastro'
    ];
    return camposData.some((token) => k.includes(token));
  }

  /** Converte dd/mm/aaaa, ISO ou yyyy-mm-dd (input date) para yyyy-mm-dd. */
  private normalizarDataFiltro(valor: string): string {
    const limpo = String(valor ?? '').trim();
    if (!limpo || limpo === '-') {
      return '';
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(limpo)) {
      return limpo;
    }
    const br = limpo.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (br) {
      return `${br[3]}-${br[2]}-${br[1]}`;
    }
    const data = new Date(limpo);
    if (Number.isNaN(data.getTime())) {
      return '';
    }
    const yyyy = data.getFullYear();
    const mm = String(data.getMonth() + 1).padStart(2, '0');
    const dd = String(data.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private formatarDataPadraoBr(valor: string): string {
    const limpo = valor.trim();
    if (!limpo) {
      return '-';
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(limpo)) {
      return limpo;
    }

    const data = new Date(limpo);
    if (Number.isNaN(data.getTime())) {
      return limpo;
    }

    const dd = String(data.getDate()).padStart(2, '0');
    const mm = String(data.getMonth() + 1).padStart(2, '0');
    const yyyy = data.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  iniciarArrasteTabela(event: MouseEvent): void {
    const el = event.currentTarget as HTMLElement;
    if (!el) {
      return;
    }
    this.arrastandoTabela = true;
    this.scrollInicioX = event.pageX - el.offsetLeft;
    this.scrollEsquerdaInicial = el.scrollLeft;
  }

  moverArrasteTabela(event: MouseEvent): void {
    if (!this.arrastandoTabela) {
      return;
    }
    const el = event.currentTarget as HTMLElement;
    if (!el) {
      return;
    }
    event.preventDefault();
    const x = event.pageX - el.offsetLeft;
    const distancia = x - this.scrollInicioX;
    el.scrollLeft = this.scrollEsquerdaInicial - distancia;
  }

  finalizarArrasteTabela(): void {
    this.arrastandoTabela = false;
  }

  private normalizarChave(valor: string): string {
    return String(valor || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  formatarServicos(servicos: string[]): string {
    if (!servicos || servicos.length === 0) return 'Nenhum serviço';
    return servicos.join(', ');
  }

  calcularEstatisticas(): void {
    this.totalClientes = this.clientes.length;
    this.clientesComPasta = this.clientes.filter((c) => c.pastaFisica).length;

    const tarefasUnicas = new Set<string>();
    this.clientes.forEach((cliente) => {
      if (!cliente.tarefa || cliente.tarefa === '-') {
        return;
      }
      cliente.tarefa
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
        .forEach((t) => tarefasUnicas.add(t));
    });
    this.totalTarefas = tarefasUnicas.size;
  }
}
